/**
 * German UI strings.
 *
 * All user-facing text lives here rather than in JSX, so a second language is a
 * matter of adding a sibling file and a lookup — not of hunting through
 * components. Field labels are NOT part of this: those come from the attribute
 * catalogue in the database and are edited in the portal.
 */
export const de = {
  app: {
    title: 'Capture Fair',
    subtitle: 'Verwaltung und Auswertung der auf Messen erfassten Artikel',
    loading: 'Wird geladen …',
  },

  nav: {
    tenants: 'Mandanten',
    users: 'Nutzer',
    valueLists: 'Stammdaten',
    pending: 'Zu prüfen',
    events: 'Messen',
    items: 'Artikel',
    attributes: 'Felder',
    exports: 'Export',
    status: 'Systemstatus',
    signOut: 'Abmelden',
    signedInAs: 'Angemeldet als',
  },

  valueLists: {
    heading: 'Stammdaten',
    intro: 'Lieferanten, Einkäufer, Saisons, Themen und Projekte pflegen.',
    loadError: 'Die Stammdaten konnten nicht geladen werden.',
    entries: 'Einträge',
    pendingBadge: '{count} zu prüfen',
    systemBadge: 'Systemliste',
    allowClientCreation: 'Neue Einträge in der App erlaubt',
    allowClientCreationHint:
      'Ist der Schalter an, kann am Messestand ein unbekannter Eintrag sofort angelegt und verwendet werden. Er erscheint danach unter „Zu prüfen".',
    open: 'Öffnen',
    createList: 'Liste anlegen',
    createListHint:
      'Eine zusätzliche Werteliste, etwa Währungen für ein neues Preisfeld. Die Einträge kommen danach von Hand oder per CSV.',
    listKey: 'Schlüssel',
    listKeyHint:
      'Technischer Name. Kleinbuchstaben, Ziffern, Unterstrich. Später nicht mehr änderbar.',
    listDescription: 'Beschreibung',

    detail: {
      back: 'Zurück zur Übersicht',
      searchLabel: 'Suche',
      searchPlaceholder: 'Bezeichnung, Wert oder Zusatzangabe',
      showArchived: 'Zusammengeführte und archivierte anzeigen',
      empty: 'Keine Einträge gefunden.',
      countOne: 'Ein Eintrag',
      countMany: '{count} Einträge',
      page: 'Seite {page} von {pages}',
      previous: 'Zurück',
      next: 'Weiter',
    },

    columns: {
      label: 'Bezeichnung',
      value: 'Wert',
      supplierNumber: 'Lieferantennummer',
      country: 'Land',
      contact: 'Kontakt',
      note: 'Notiz',
      sortOrder: 'Sortierung',
      status: 'Status',
      usage: 'Verwendet',
      createdBy: 'Angelegt von',
      actions: 'Aktionen',
    },

    status: {
      active: 'Aktiv',
      pending_approval: 'Zu prüfen',
      merged: 'Zusammengeführt',
      archived: 'Archiviert',
    },

    mergedInto: 'Zusammengeführt mit {label}',

    actions: {
      create: 'Eintrag anlegen',
      edit: 'Bearbeiten',
      merge: 'Zusammenführen',
      archive: 'Archivieren',
      restore: 'Wiederherstellen',
      import: 'CSV importieren',
      save: 'Speichern',
      saving: 'Wird gespeichert …',
      cancel: 'Abbrechen',
    },

    form: {
      createHeading: 'Eintrag anlegen',
      editHeading: 'Eintrag bearbeiten',
      label: 'Bezeichnung',
      value: 'Wert',
      valueHint: 'Wird im Export ausgegeben. Leer lassen, um die Bezeichnung zu übernehmen.',
      sortOrder: 'Sortierung',
      created: 'Der Eintrag wurde angelegt.',
      updated: 'Die Änderungen wurden gespeichert.',
      duplicate: 'Ein Eintrag mit diesem Wert existiert bereits in dieser Liste.',
    },

    merge: {
      heading: 'Einträge zusammenführen',
      description:
        '„{source}" verschwindet aus der Liste. Alle Artikel und Zuordnungen, die darauf verweisen, werden auf den Zieleintrag umgehängt.',
      target: 'Zieleintrag (bleibt bestehen)',
      targetList: 'Auswahl des Zieleintrags',
      targetPlaceholder: 'Eintrag suchen …',
      warning:
        'Das lässt sich nicht rückgängig machen. Der Quelleintrag bleibt als Verweis erhalten, damit Geräte mit der alten ID ihn noch auflösen können.',
      submit: 'Zusammenführen',
      success:
        'Zusammengeführt. Umgehängt: {items} Artikel über den Lieferanten, {attributes} über dynamische Felder, {users} Nutzerzuordnungen.',
      noTarget: 'Bitte einen Zieleintrag wählen.',
    },

    import: {
      heading: 'CSV importieren',
      step1: 'Datei wählen',
      step2: 'Spalten zuordnen',
      step3: 'Ergebnis',
      file: 'CSV-Datei',
      hasHeaderRow: 'Erste Zeile enthält Spaltenüberschriften',
      detected: 'Erkannt: {encoding}, Trennzeichen „{delimiter}"',
      detectedHint:
        'Stimmen die Umlaute in der Vorschau nicht, bitte das Encoding von Hand wählen.',
      encoding: 'Encoding',
      encodingAuto: 'Automatisch erkennen',
      preview: 'Vorschau',
      previewRows: '{count} Zeilen insgesamt',
      mapping: 'Zuordnung',
      mapLabel: 'Bezeichnung',
      mapValue: 'Wert',
      mapSortOrder: 'Sortierung',
      mapNone: '— nicht importieren —',
      onDuplicate: 'Wenn der Eintrag bereits existiert',
      onDuplicateSkip: 'Überspringen',
      onDuplicateUpdate: 'Aktualisieren',
      submit: 'Importieren',
      importing: 'Import läuft …',
      resultSummary: '{created} angelegt, {updated} aktualisiert, {skipped} übersprungen.',
      issuesHeading: '{count} Zeilen konnten nicht importiert werden',
      issueLine: 'Zeile {line}',
      close: 'Schließen',
      noFile: 'Bitte eine Datei wählen.',
      noLabelColumn: 'Bitte die Spalte mit der Bezeichnung zuordnen.',
    },
  },

  pending: {
    heading: 'Zu prüfen',
    intro:
      'Einträge, die unterwegs in der App angelegt wurden. Bis zur Freigabe sind sie nur auf dem Gerät sichtbar, das sie erzeugt hat.',
    empty: 'Es liegt nichts zur Prüfung vor.',
    loadError: 'Die Liste konnte nicht geladen werden.',
    columns: {
      list: 'Liste',
      label: 'Bezeichnung',
      createdBy: 'Angelegt von',
      createdAt: 'Angelegt am',
      usage: 'Bereits verwendet',
    },
    actions: {
      approve: 'Freigeben',
      editAndApprove: 'Bearbeiten und freigeben',
      merge: 'Zusammenführen',
      reject: 'Ablehnen',
    },
    approve: {
      success: 'Der Eintrag wurde freigegeben.',
    },
    reject: {
      heading: 'Eintrag ablehnen',
      description:
        '„{label}" wird archiviert. Bereits erfasste Artikel behalten den Verweis und bleiben lesbar — abgelehnt heißt „nicht mehr anbieten", nicht „löschen".',
      reason: 'Grund (optional)',
      warning: 'Achtung: {count} Artikel verweisen bereits auf diesen Eintrag.',
      submit: 'Ablehnen',
      success: 'Der Eintrag wurde abgelehnt.',
    },
  },

  login: {
    heading: 'Anmelden',
    intro: 'Bitte melden Sie sich mit Ihren Zugangsdaten an.',
    email: 'E-Mail-Adresse',
    password: 'Passwort',
    submit: 'Anmelden',
    submitting: 'Anmeldung läuft …',
    failed: 'Anmeldung fehlgeschlagen',
    rateLimited: 'Zu viele Anmeldeversuche. Bitte warten Sie einen Moment.',
    networkError: 'Der Server ist nicht erreichbar. Bitte prüfen Sie die Verbindung.',
  },

  users: {
    heading: 'Nutzerverwaltung',
    intro: 'Konten anlegen, Rollen vergeben und Zugänge sperren.',
    searchLabel: 'Suche',
    searchPlaceholder: 'Name oder E-Mail',
    showInactive: 'Deaktivierte anzeigen',
    empty: 'Keine Nutzer gefunden.',
    loadError: 'Die Nutzerliste konnte nicht geladen werden.',
    countOne: 'Ein Nutzer',
    countMany: '{count} Nutzer',

    columns: {
      name: 'Name',
      email: 'E-Mail',
      role: 'Rolle',
      status: 'Status',
      lastLogin: 'Letzte Anmeldung',
      actions: 'Aktionen',
    },

    roles: {
      // "Administrator" hieß es, solange es nur einen Mandanten gab. Ein
      // Teamleiter verwaltet sein Team, seine Messen und seinen Feldkatalog —
      // und sieht von anderen Mandanten nichts.
      platform: 'Plattform-Admin',
      admin: 'Teamleiter',
      capturer: 'Erfasser',
    },

    status: {
      active: 'Aktiv',
      inactive: 'Deaktiviert',
    },

    neverLoggedIn: 'noch nie',

    actions: {
      create: 'Nutzer anlegen',
      edit: 'Bearbeiten',
      resetPassword: 'Passwort zurücksetzen',
      deactivate: 'Deaktivieren',
      activate: 'Aktivieren',
      save: 'Speichern',
      saving: 'Wird gespeichert …',
      cancel: 'Abbrechen',
    },

    create: {
      heading: 'Neuen Nutzer anlegen',
      description:
        'Das Konto ist sofort nutzbar. Das Passwort übergeben Sie der Person auf einem sicheren Weg — es wird nicht per E-Mail versendet.',
      name: 'Name',
      email: 'E-Mail-Adresse',
      password: 'Passwort',
      passwordHint: 'Mindestens 12 Zeichen.',
      generate: 'Passwort erzeugen',
      role: 'Rolle',
      buyer: 'Einkäufer-Zuordnung',
      buyerHint:
        'Bestimmt, mit welchem Einkäufer die App das Buyer-Feld vorbelegt. Der Erfasser kann davon abweichen.',
      buyerNone: '— keine Zuordnung —',
      submit: 'Anlegen',
      success: 'Der Nutzer wurde angelegt.',
      duplicate: 'Zu dieser E-Mail-Adresse existiert bereits ein Konto.',
    },

    edit: {
      heading: 'Nutzer bearbeiten',
      emailImmutable:
        'Die E-Mail-Adresse ist unveränderlich, weil sie das Konto in Protokollen und Erfassungen identifiziert.',
      success: 'Die Änderungen wurden gespeichert.',
    },

    resetPassword: {
      heading: 'Passwort zurücksetzen',
      description: 'Setzt ein neues Passwort für {name}.',
      newPassword: 'Neues Passwort',
      revokeSessions: 'Alle Sitzungen dieses Kontos beenden',
      revokeHint: 'Empfohlen: Die Person muss sich auf allen Geräten neu anmelden.',
      submit: 'Zurücksetzen',
      success: 'Das Passwort wurde zurückgesetzt.',
      copyHint: 'Notieren Sie das Passwort jetzt — es wird nicht erneut angezeigt.',
      copy: 'Kopieren',
      copied: 'Kopiert',
    },

    deactivate: {
      heading: 'Nutzer deaktivieren',
      description:
        'Möchten Sie {name} deaktivieren? Alle Sitzungen werden sofort beendet, erfasste Artikel bleiben erhalten.',
      confirm: 'Deaktivieren',
      success: 'Der Nutzer wurde deaktiviert.',
    },

    activate: {
      success: 'Der Nutzer wurde wieder aktiviert.',
    },
  },

  status: {
    heading: 'Systemstatus',
    checking: 'Verbindung zur API wird geprüft …',
    online: 'API erreichbar',
    offline: 'API nicht erreichbar',
    version: 'Version',
    commit: 'Commit',
    environment: 'Umgebung',
    database: 'Datenbank',
    databaseOk: 'verbunden',
    databaseError: 'nicht verbunden',
    retry: 'Erneut prüfen',
  },

  errors: {
    unexpected: 'Es ist ein unerwarteter Fehler aufgetreten.',
    forbidden: 'Für diese Aktion fehlen Ihnen die erforderlichen Rechte.',
    notFound: 'Die angeforderte Seite existiert nicht.',
    required: 'Dieses Feld ist erforderlich.',
  },

  common: {
    close: 'Schließen',
    back: 'Zurück',
    edit: 'Bearbeiten',
    delete: 'Löschen',
    cancel: 'Abbrechen',
    save: 'Sichern',
  },
  tenants: {
    title: 'Mandanten',
    subtitle: 'Teams anlegen und verwalten',
    navLabel: 'Mandanten',
    loadError: 'Die Mandanten konnten nicht geladen werden.',

    create: 'Mandant anlegen',
    edit: 'Bearbeiten',
    save: 'Sichern',
    saving: 'Wird gesichert …',
    cancel: 'Abbrechen',
    created: 'Der Mandant wurde angelegt.',
    saved: 'Der Mandant wurde gespeichert.',

    name: 'Name',
    slug: 'Kürzel',
    slugHint:
      'Kleinbuchstaben, Ziffern und Bindestriche. Erscheint in Exporten und im Support — nach dem Anlegen nicht mehr änderbar.',
    active: 'Aktiv',
    activeHint:
      'Ein stillgelegter Mandant kann sich nicht mehr anmelden — sofort, auch mit einer offenen Sitzung. Seine Daten bleiben unberührt.',
    inactive: 'Stillgelegt',
    showInactive: 'Stillgelegte anzeigen',

    leadHeading: 'Erste Teamleitung',
    leadHint:
      'Ohne Teamleitung kann sich niemand in diesen Mandanten anmelden, deshalb wird sie gleich mit angelegt.',
    leadName: 'Name',
    leadEmail: 'E-Mail-Adresse',

    passwordHeading: 'Zugangsdaten — nur jetzt sichtbar',
    passwordHint:
      'Das Passwort wird nirgends im Klartext gespeichert und lässt sich später nicht wieder anzeigen. Jetzt weitergeben oder neu setzen lassen.',
    passwordDone: 'Notiert',

    counts: '{users} Konten · {events} Messen · {items} Artikel',
    empty: 'Noch kein Mandant angelegt.',
    countOne: 'Ein Mandant',
    countMany: '{count} Mandanten',
  },

  events: {
    title: 'Messen',
    subtitle: 'Anlegen, benennen und stilllegen',
    navLabel: 'Messen',
    loadError: 'Die Messen konnten nicht geladen werden.',

    create: 'Messe anlegen',
    edit: 'Bearbeiten',
    save: 'Sichern',
    saving: 'Wird gesichert …',
    cancel: 'Abbrechen',
    saved: 'Die Messe wurde gespeichert.',
    created: 'Die Messe wurde angelegt.',
    delete: 'Löschen',
    deleteConfirm: 'Diese Messe löschen? Das geht nur, solange kein Artikel daran hängt.',
    deletedNotice: 'Die Messe wurde gelöscht.',

    code: 'Kürzel',
    codeHint:
      'Steckt in jeder Artikelkennung dieser Messe — aus AMB27 wird AMB27-0001. Großbuchstaben und Ziffern. Nach der ersten Erfassung nicht mehr änderbar.',
    codeFrozen:
      'Nicht mehr änderbar: Artikel tragen bereits eine Kennung mit diesem Kürzel, und die steht auf den Mustern.',
    name: 'Name',
    location: 'Ort',
    startsOn: 'Beginn',
    endsOn: 'Ende',
    active: 'Aktiv',
    activeHint:
      'Inaktive Messen verschwinden aus den Auswahllisten. Die erfassten Artikel bleiben unberührt.',

    inactive: 'Stillgelegt',
    showInactive: 'Stillgelegte anzeigen',
    countOne: 'Eine Messe',
    countMany: '{count} Messen',
    empty: 'Noch keine Messe angelegt.',
    noDates: 'Kein Zeitraum hinterlegt',
    itemCount: '{count} Artikel ansehen',
    noItems: 'Noch nichts erfasst',

    // --- Feldkatalog ---
    schemaVersion: 'Feldkatalog Version {version}',
    schemaVersionNone: 'Kein Feldkatalog zugewiesen',
    schemaVersionBehind: 'Feldkatalog Version {version} — neuer wäre {latest}',
    schemaVersionHint:
      'Beim Anlegen festgehalten. Wird im Designer veröffentlicht, ändert sich das Formular auf den Geräten dieser Messe nicht — bis du sie hier ausdrücklich anhebst.',
    schemaVersionCurrent: 'Diese Messe nutzt den neuesten Feldkatalog.',
    lift: 'Auf Version {latest} heben',
    liftConfirm:
      'Diese Messe auf Feldkatalog Version {latest} heben? Das Formular auf allen Geräten dieser Messe ändert sich beim nächsten Abgleich — auch mitten in der Messe.',
    lifted: 'Die Messe nutzt jetzt den neuen Feldkatalog.',
  },

  items: {
    title: 'Artikel',
    subtitle: 'Alles, was auf den Messen erfasst wurde',
    navLabel: 'Artikel',
    loadError: 'Die Artikel konnten nicht geladen werden.',
    notFound: 'Dieser Artikel existiert nicht.',

    search: 'Suche',
    searchHint: 'Kennung, Barcode oder ein Wort aus den Werten.',
    filters: 'Filter',
    clearFilters: 'Filter zurücksetzen',
    filterEvent: 'Messe',
    filterSupplier: 'Lieferant',
    filterCapturer: 'Erfasst von',
    filterFrom: 'Erfasst ab',
    filterTo: 'Erfasst bis',
    allEvents: 'Alle Messen',
    allSuppliers: 'Alle Lieferanten',
    allCapturers: 'Alle Personen',
    onlyNeedsReview: 'Nur nachzupflegende',
    includeDeleted: 'Gelöschte anzeigen',
    includeDeletedHint:
      'Gelöschte Artikel bleiben als Grabstein erhalten und lassen sich wiederherstellen.',

    countOne: 'Ein Artikel',
    countMany: '{count} Artikel',
    empty: 'Noch nichts erfasst.',
    emptyFiltered: 'Kein Artikel passt zu diesen Filtern.',
    page: 'Seite {page} von {pages}',
    previous: 'Zurück',
    next: 'Weiter',

    shortCode: 'Kennung',
    noShortCode: 'ohne Kennung',
    event: 'Messe',
    eventHint: 'Später nicht mehr änderbar — die laufende Kennung hängt daran.',
    chooseEvent: 'Bitte wählen',
    supplier: 'Lieferant',
    noSupplier: 'Keiner',
    barcode: 'Barcode',
    photos: 'Fotos',
    noPhotos: 'Keine Fotos.',
    photosOnDevice:
      'Fotos entstehen am Gerät. Ein von Hand angelegter Artikel bekommt sie beim nächsten Abgleich, wenn jemand ihn am Telefon ergänzt.',
    photoPending: 'Noch nicht hochgeladen',
    captured: 'Erfasst',
    capturedBy: '{event} · {person} · {date}',
    needsReview: 'Nachzupflegen',
    needsReviewLabel: 'Nachzupflegen',
    needsReviewHint:
      'Gesetzt, wenn ein Feld erst nach der Erfassung zur Pflicht wurde. Nach dem Nachtragen abwählen.',
    deleted: 'Gelöscht',
    deletedHint:
      'Dieser Artikel ist gelöscht. Er zählt in keinem Export mit und verschwindet beim nächsten Abgleich von den Geräten. Wiederherstellen ist möglich, solange die Zeile steht.',
    backToList: 'Zurück zur Liste',
    values: 'Werte',
    schemaVersion: 'Katalogversion',
    revision: 'Änderungsstand',
    updatedAt: 'Zuletzt geändert',

    create: 'Artikel anlegen',
    edit: 'Bearbeiten',
    save: 'Sichern',
    saving: 'Wird gesichert …',
    cancel: 'Abbrechen',
    saved: 'Der Artikel wurde gespeichert.',
    delete: 'Löschen',
    deleteConfirm:
      'Diesen Artikel löschen? Er verschwindet aus Liste und Export und beim nächsten Abgleich von den Geräten. Rückgängig machen ist möglich.',
    deletedNotice: 'Der Artikel wurde gelöscht.',
    restore: 'Wiederherstellen',
    restoredNotice: 'Der Artikel wurde wiederhergestellt.',
  },

  attributes: {
    title: 'Felder',
    subtitle: 'Der Feldkatalog, aus dem die App ihr Erfassungsformular baut',
    navLabel: 'Felder',
    loadError: 'Der Feldkatalog konnte nicht geladen werden.',

    newField: 'Feld anlegen',
    showInactive: 'Deaktivierte anzeigen',
    noFields: 'Noch kein Feld im Katalog',
    fieldCount: '{count} Felder',

    // --- Veröffentlichen ---
    version: 'Version {version}',
    neverPublished: 'Noch nie veröffentlicht',
    publishedAt: 'Veröffentlicht am {date}',
    unpublished: 'Änderungen sind gespeichert, aber noch nicht auf den Geräten.',
    unpublishedHint:
      'Die App baut ihr Formular aus der veröffentlichten Version. Bis zur Veröffentlichung erfasst sie weiter mit dem alten Katalog.',
    publish: 'Veröffentlichen',
    publishing: 'Wird veröffentlicht …',
    publishNotes: 'Notiz zur Version',
    publishNotesHint: 'Wofür diese Version steht. Erscheint später im Verlauf.',
    published: 'Version {version} veröffentlicht.',
    nothingToPublish: 'Nichts zu veröffentlichen — der Katalog ist unverändert.',
    publishWarning:
      'Während einer laufenden Messe nicht veröffentlichen: Die Geräte übernehmen das neue Formular beim nächsten Abgleich, mitten in der Erfassung.',

    // --- Feld ---
    key: 'Schlüssel',
    keyHint: 'Technischer Name. Kleinbuchstaben, Ziffern, Unterstrich. Später nicht mehr änderbar.',
    label: 'Bezeichnung',
    labelHint: 'Was am Gerät über dem Feld steht.',
    type: 'Typ',
    helpText: 'Hilfetext',
    helpTextHint: 'Erscheint klein unter dem Feld. Leer lassen, wenn die Bezeichnung reicht.',
    group: 'Gruppe',
    groupHint: 'Felder derselben Gruppe stehen im Formular zusammen. Leer lassen für keine.',
    required: 'Pflichtfeld',
    carryOver: 'Wert in den nächsten Artikel übernehmen',
    carryOverHint: 'Spart am Stand jeden zweiten Tastendruck, wenn viele Artikel dasselbe teilen.',
    showInList: 'In der Artikelliste anzeigen',
    valueList: 'Werteliste',
    valueListHint: 'Woher die Auswahl kommt.',
    noValueList: 'Bitte wählen',
    defaultValue: 'Vorbelegung',

    // --- Regeln ---
    rules: 'Regeln',
    minLength: 'Mindestlänge',
    maxLength: 'Höchstlänge',
    pattern: 'Muster (regulärer Ausdruck)',
    patternMessage: 'Meldung bei Verstoß',
    min: 'Kleinster Wert',
    max: 'Größter Wert',
    precision: 'Stellen gesamt',
    scale: 'Nachkommastellen',
    minItems: 'Mindestens auszuwählen',
    maxItems: 'Höchstens auszuwählen',
    minCount: 'Mindestens Fotos',
    maxCount: 'Höchstens Fotos',
    symbologies: 'Erlaubte Symbologien',
    symbologiesHint: 'Kommagetrennt. Leer heißt: alle, die der Scanner kann.',
    noRules: 'Für diesen Typ gibt es nichts einzustellen.',

    // --- Status ---
    systemField: 'Systemfeld',
    systemFieldHint:
      'Schlüssel, Typ und Pflichtangabe sind festgelegt, weil Abgleich, Duplikaterkennung und Export darauf aufbauen. Bezeichnung, Hilfetext, Gruppe und Sortierung lassen sich ändern.',
    autoField: 'Wird automatisch gefüllt',
    inactive: 'Deaktiviert',

    // --- Aktionen ---
    save: 'Sichern',
    saving: 'Wird gesichert …',
    cancel: 'Abbrechen',
    deactivate: 'Deaktivieren',
    reactivate: 'Wieder aktivieren',
    deactivateConfirm:
      'Dieses Feld deaktivieren? Es verschwindet aus Formular und Spaltenwähler. Bereits erfasste Werte bleiben erhalten und behalten ihre Bedeutung.',
    moveUp: 'Nach oben',
    moveDown: 'Nach unten',
    dragHint: 'Zum Sortieren ziehen',
    orderSaved: 'Reihenfolge gespeichert.',
    saved: 'Feld gespeichert.',
    created: 'Feld angelegt.',

    // --- Vorschau ---
    preview: 'Vorschau',
    previewHint: 'So baut die App das Formular aus dem aktuellen Stand.',
    previewUngrouped: 'Ohne Gruppe',
    previewAuto: 'wird automatisch gefüllt',
    previewPhoto: 'Fotos aufnehmen',
    previewScan: 'Scannen oder eingeben',
    previewSelect: 'Auswählen',
    previewEmpty: 'Kein aktives Feld — das Formular wäre leer.',

    // --- Typen ---
    types: {
      text: 'Text',
      longtext: 'Langer Text',
      number: 'Zahl',
      decimal: 'Dezimalzahl',
      integer: 'Ganze Zahl',
      boolean: 'Ja/Nein',
      date: 'Datum',
      datetime: 'Datum und Uhrzeit',
      select: 'Auswahl',
      multiselect: 'Mehrfachauswahl',
      barcode: 'Barcode',
      photo: 'Fotos',
      user: 'Person',
    },
  },

  exports: {
    title: 'Export',
    subtitle: 'Profile zusammenstellen und Erfassungen ausgeben',
    navLabel: 'Export',

    profiles: 'Profile',
    noProfiles: 'Noch kein Profil angelegt',
    noProfilesHint:
      'Ein Profil legt fest, welche Spalten in welcher Reihenfolge ausgegeben werden und wie Werte formatiert sind.',
    newProfile: 'Neues Profil',
    editProfile: 'Profil bearbeiten',
    duplicate: 'Duplizieren',
    duplicateName: 'Name der Kopie',
    profileName: 'Name',
    profileDescription: 'Beschreibung',
    deleteProfile: 'Profil löschen',
    deleteProfileConfirm: 'Dieses Profil löschen? Vergangene Exporte bleiben nachvollziehbar.',

    columns: 'Spalten',
    availableColumns: 'Verfügbare Felder',
    selectedColumns: 'Ausgewählte Spalten',
    noColumns: 'Noch keine Spalte gewählt',
    noColumnsHint: 'Wähle links Felder aus. Die Reihenfolge hier ist die Reihenfolge in der Datei.',
    coreFields: 'Kernfelder',
    catalogueFields: 'Felder aus dem Katalog',
    header: 'Überschrift',
    moveUp: 'Nach oben',
    moveDown: 'Nach unten',
    removeColumn: 'Spalte entfernen',
    dragHint: 'Zum Sortieren ziehen',

    formatting: 'Formatierung',
    dateFormat: 'Datumsformat',
    decimalSeparator: 'Dezimaltrennzeichen',
    thousandsSeparator: 'Tausendertrennzeichen',
    emptyValue: 'Ersatz für leere Werte',
    prefix: 'Präfix',
    suffix: 'Suffix',
    multiValueSeparator: 'Trennzeichen bei Mehrfachwerten',
    booleanTrue: 'Text für Ja',
    booleanFalse: 'Text für Nein',
    valueListRendering: 'Darstellung von Verweisen',
    renderLabel: 'Bezeichnung',
    renderValue: 'Technischer Wert',
    renderMetadata: 'Zusatzfeld',
    metadataKey: 'Name des Zusatzfelds',
    userRendering: 'Darstellung von Personen',
    renderName: 'Name',
    renderEmail: 'E-Mail',
    caseTransform: 'Groß-/Kleinschreibung',
    caseNone: 'Unverändert',
    caseUpper: 'GROSSBUCHSTABEN',
    caseLower: 'kleinbuchstaben',

    dialect: 'Dateiformat',
    format: 'Format',
    delimiter: 'Trennzeichen',
    lineEnding: 'Zeilenende',
    encoding: 'Zeichensatz',
    quoting: 'Anführungszeichen',
    quotingMinimal: 'Nur wo nötig',
    quotingAlways: 'Immer',
    includeHeader: 'Kopfzeile ausgeben',
    includePhotos: 'Fotos beilegen',
    encodingHint:
      'UTF-8 mit BOM öffnet in Excel auf deutschen Windows-Rechnern korrekt. Ohne BOM erscheinen Umlaute zerschossen.',

    preview: 'Vorschau',
    previewHint: 'Die ersten {count} Zeilen, gerendert wie im echten Export.',
    previewEmpty: 'Keine Erfassungen im gewählten Zeitraum',
    previewOf: '{shown} von {total} Artikeln',
    refreshPreview: 'Vorschau aktualisieren',

    run: 'Exportieren',
    running: 'Wird erzeugt …',
    filters: 'Filter',
    filterEvent: 'Messe',
    filterSupplier: 'Lieferant',
    filterCapturedFrom: 'Erfasst ab',
    filterCapturedTo: 'Erfasst bis',
    filterIncludeNeedsReview: 'Auch nachzupflegende Artikel',
    allEvents: 'Alle Messen',
    allSuppliers: 'Alle Lieferanten',

    history: 'Verlauf',
    noRuns: 'Noch kein Export erzeugt',
    repeat: 'Erneut herunterladen',
    repeatHint: 'Erzeugt die Datei mit dem damaligen Profil, nicht mit dem heutigen.',
    runRows: '{count} Artikel',
    runPhotos: '{count} Fotos',
    runFailed: 'Fehlgeschlagen',
    runSucceeded: 'Erfolgreich',

    unmappableWarning:
      'Diese Zeichen lassen sich im gewählten Zeichensatz nicht darstellen und wurden durch ? ersetzt: {characters}',
    missingPhotosWarning: '{count} Fotos fehlen im Objektspeicher und sind nicht im Archiv.',
    saved: 'Profil gespeichert',
  },
} as const

export type Translations = typeof de
